import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, CheckCircle, Trash2, TrendingUp, History, Clock } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { todayCR } from "../utils/dateHelpers";
import CustomerHistoryDialog from "../components/CustomerHistoryDialog";
import { DebtDiscountDialog, DebtReturnDialog } from "../components/DebtActionsDialogs";

export default function Debts({ user }) {
  const { selectedBox } = useBox();
  // A cashier on a box flagged `acts_as_master` gets the same UI affordances
  // (Adjust, Delete, "Días anteriores") as a true MASTER user.
  const hasMasterRights = user?.role === "master" || !!selectedBox?.acts_as_master;
  const [debts, setDebts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [showIncreaseDialog, setShowIncreaseDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMovementsDialog, setShowMovementsDialog] = useState(false);
  const [showFullHistoryDialog, setShowFullHistoryDialog] = useState(false);
  const [fullHistoryTarget, setFullHistoryTarget] = useState(null); // {id, name}
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [activeTab, setActiveTab] = useState("pendiente");
  const [paymentType, setPaymentType] = useState("venta");
  // Datáfono para pagos con tarjeta
  const [debtDatafonos, setDebtDatafonos] = useState([]);
  const [selectedDebtDatafonoId, setSelectedDebtDatafonoId] = useState("");
  const [debtDatafonoReference, setDebtDatafonoReference] = useState("");
  const [debtCardType, setDebtCardType] = useState("credito");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fullMovements, setFullMovements] = useState([]);
  const [movementsDate, setMovementsDate] = useState("");
  const [customerMovements, setCustomerMovements] = useState([]);
  const [vaults, setVaults] = useState([]);  // NEW: List of vaults for SINPE
  const [selectedVault, setSelectedVault] = useState("");  // NEW: Selected vault for payment
  const [paymentReference, setPaymentReference] = useState(""); // SINPE/Banco comprobante
  const [cashRegisterOpeningDate, setCashRegisterOpeningDate] = useState(null);  // Opening date of current cash register
  
  // Cleanup on unmount to prevent removeChild errors
  useEffect(() => {
    return () => {
      // Close all dialogs on unmount
      setShowDialog(false);
      setShowPayDialog(false);
      setShowIncreaseDialog(false);
      setShowAdjustDialog(false);
      setShowHistoryDialog(false);
      setShowDeleteDialog(false);
      setShowMovementsDialog(false);
    };
  }, []);
  
  const [paymentData, setPaymentData] = useState({
    amount_paid_colones: 0,
    amount_paid_dollars: 0,
    payment_note: ""
  });
  
  const [paymentScope, setPaymentScope] = useState("all"); // "previous_days" or "all"
  const [debtsSummary, setDebtsSummary] = useState({
    today: { count: 0, colones: 0, dollars: 0, debts: [] },
    previousDays: { count: 0, colones: 0, dollars: 0, debts: [] }
  });
  
  const [increaseData, setIncreaseData] = useState({
    amount_colones: 0,
    amount_dollars: 0,
    reason: ""
  });
  
  const [adjustData, setAdjustData] = useState({
    amount_colones: 0,
    amount_dollars: 0,
    reason: ""
  });
  
  const [customerSearch, setCustomerSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState(""); // For filtering the list
  const [selectedDate, setSelectedDate] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "",
    type: "por_cobrar",
    amount_colones: 0,
    amount_dollars: 0,
    description: "",
    is_previous_days: false,  // Nueva opción para marcar como días anteriores
    previous_days_colones: 0,  // Monto de días anteriores
    previous_days_dollars: 0   // Monto de días anteriores en dólares
  });

  const fetchDebts = async () => {
    try {
      const url = selectedBox ? `/debts?box_id=${selectedBox.id}` : '/debts';
      console.log("Fetching debts from:", url);
      const response = await axiosInstance.get(url);
      console.log("Debts response:", response.data.length, "deudas");
      setDebts(response.data);
    } catch (error) {
      console.error("Error fetching debts:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const url = selectedBox ? `/customers?box_id=${selectedBox.id}` : '/customers';
      const response = await axiosInstance.get(url);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vaults for SINPE payments
  const fetchVaults = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/banks?box_id=${selectedBox.id}`);
      console.log("Banks loaded for box:", response.data);
      setVaults(response.data);
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  };

  // Fetch cash register opening date - CRITICAL for date comparisons
  const fetchCashRegisterOpeningDate = async () => {
    if (!selectedBox) {
      setCashRegisterOpeningDate(null);
      return null;
    }
    try {
      const response = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
      if (response.data && response.data.opened_at) {
        const openingDate = response.data.opened_at.split('T')[0];
        setCashRegisterOpeningDate(openingDate);
        console.log("Cash register opening date loaded:", openingDate);
        return openingDate;
      }
    } catch (error) {
      console.error("Error fetching cash register:", error);
    }
    setCashRegisterOpeningDate(null);
    return null;
  };

  // Helper function to get the working date (opening date or Costa Rica fallback)
  const getWorkingDate = () => {
    if (cashRegisterOpeningDate) {
      return cashRegisterOpeningDate;
    }
    // Fallback: Costa Rica timezone current date
    const now = new Date();
    const crOffset = -6 * 60;
    const utcOffset = now.getTimezoneOffset();
    const crTime = new Date(now.getTime() + (utcOffset + crOffset) * 60000);
    return `${crTime.getFullYear()}-${String(crTime.getMonth() + 1).padStart(2, '0')}-${String(crTime.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!selectedBox) return;
    (async () => {
      await fetchDebts();
      await fetchCustomers();
      await fetchVaults();
      await fetchCashRegisterOpeningDate();
      try {
        const r = await axiosInstance.get(`/datafonos?box_id=${selectedBox.id}&active=true`);
        setDebtDatafonos(r.data || []);
      } catch (e) { setDebtDatafonos([]); }
    })();
  }, [selectedBox]);

  // Cargar todas las deudas al inicio (para usuarios MASTER)
  useEffect(() => {
    if (!user) return;
    (async () => { await fetchDebts(); })();
  }, [user]);

  const handleCreateDebt = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    try {
      const selectedCustomer = customers.find(c => c.id === formData.customer_id);
      const debtData = {
        ...formData,
        customer_name: selectedCustomer ? selectedCustomer.name : formData.customer_name,
        box_id: selectedBox.id
      };
      
      await axiosInstance.post('/debts', debtData);
      toast.success("Cuenta registrada exitosamente");
      setShowDialog(false);
      setFormData({
        customer_id: "",
        customer_name: "",
        type: "por_cobrar",
        amount_colones: 0,
        amount_dollars: 0,
        description: "",
        is_previous_days: false,
        previous_days_colones: 0,
        previous_days_dollars: 0
      });
      fetchDebts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar cuenta"));
    }
  };

  const openPayDialog = async (debt) => {
    setSelectedDebt(debt);
    setPaymentType("venta"); // Inicializar tipo de pago
    
    // Agrupar todas las deudas pendientes del cliente
    const customerDebts = debts.filter(d => 
      d.customer_name === debt.customer_name && 
      d.type === debt.type &&
      d.status === "pendiente"
    );
    
    console.log("=== INICIO ANÁLISIS CON SNAPSHOTS ===");
    console.log("Total deudas del cliente:", customerDebts.length);
    
    // CRITICAL FIX: Use the OPENING DATE of the current cash register
    // If no register is open, use the debt's own snapshot_date as reference
    let today = cashRegisterOpeningDate;
    if (!today) {
      today = await fetchCashRegisterOpeningDate();
    }
    
    // If still no date from register, use the DEBT's snapshot_date as reference
    // This ensures we always split correctly based on the debt's own data
    if (!today && debt.snapshot_date) {
      today = debt.snapshot_date;
      console.log("Sin caja abierta, usando snapshot_date de la deuda:", today);
    }
    
    if (!today) {
      // Final fallback: Costa Rica timezone current date
      const now = new Date();
      const crOffset = -6 * 60;
      const utcOffset = now.getTimezoneOffset();
      const crTime = new Date(now.getTime() + (utcOffset + crOffset) * 60000);
      today = `${crTime.getFullYear()}-${String(crTime.getMonth() + 1).padStart(2, '0')}-${String(crTime.getDate()).padStart(2, '0')}`;
      console.log("Fallback final: usando fecha CR:", today);
    }
    console.log("Fecha para comparación de snapshot:", today);
    
    // Calcular deudas usando snapshots
    let todayColones = 0;
    let todayDollars = 0;
    let previousColones = 0;
    let previousDollars = 0;
    
    const todayDebts = [];
    const previousDaysDebts = [];
    
    customerDebts.forEach(d => {
      // Usar balance_colones (saldo actual) en lugar de amount_colones (monto original)
      const currentBalance = d.balance_colones || d.amount_colones;
      const currentBalanceDollars = d.balance_dollars || d.amount_dollars;
      
      // DEBUG: Log all relevant fields
      console.log(`DEBUG Deuda ${d.customer_name}:`, {
        snapshot_date: d.snapshot_date,
        today: today,
        match: d.snapshot_date === today,
        saldo_inicio_dia_colones: d.saldo_inicio_dia_colones,
        saldo_is_null: d.saldo_inicio_dia_colones === null,
        saldo_is_undefined: d.saldo_inicio_dia_colones === undefined,
        balance_colones: d.balance_colones,
        amount_colones: d.amount_colones,
        currentBalance: currentBalance
      });
      
      // Si tiene snapshot del día de hoy, usarlo
      // Cambiar la condición para aceptar 0 como valor válido
      if (d.snapshot_date === today && d.saldo_inicio_dia_colones !== null && d.saldo_inicio_dia_colones !== undefined) {
        // Deuda de días anteriores = snapshot (pero no mayor que el balance actual)
        const previousAmount = {
          colones: Math.min(d.saldo_inicio_dia_colones, currentBalance),
          dollars: Math.min(d.saldo_inicio_dia_dollars || 0, currentBalanceDollars)
        };
        
        // Deuda de hoy = balance actual - días anteriores
        const todayAmount = {
          colones: Math.max(0, currentBalance - previousAmount.colones),
          dollars: Math.max(0, currentBalanceDollars - previousAmount.dollars)
        };
        
        console.log(`Deuda ${d.id} (CON SNAPSHOT):`, {
          balance: `₡${currentBalance}`,
          snapshot: `₡${d.saldo_inicio_dia_colones}`,
          anterior: `₡${previousAmount.colones}`,
          hoy: `₡${todayAmount.colones}`
        });
        
        // Agregar a días anteriores si hay monto
        if (previousAmount.colones > 0 || previousAmount.dollars > 0) {
          previousColones += previousAmount.colones;
          previousDollars += previousAmount.dollars;
          previousDaysDebts.push({
            ...d,
            amount_colones: previousAmount.colones,
            amount_dollars: previousAmount.dollars,
            period: "previous_days"
          });
        }
        
        // Agregar a hoy si hay monto
        if (todayAmount.colones > 0 || todayAmount.dollars > 0) {
          todayColones += todayAmount.colones;
          todayDollars += todayAmount.dollars;
          todayDebts.push({
            ...d,
            amount_colones: todayAmount.colones,
            amount_dollars: todayAmount.dollars,
            period: "today"
          });
        }
      } else {
        // Sin snapshot: toda la deuda es de días anteriores
        console.log(`Deuda ${d.id} (SIN SNAPSHOT): ₡${currentBalance} (días anteriores)`);
        previousColones += currentBalance;
        previousDollars += currentBalanceDollars;
        previousDaysDebts.push({
          ...d,
          amount_colones: currentBalance,
          amount_dollars: currentBalanceDollars,
          period: "previous_days"
        });
      }
    });
    
    const summary = {
      today: {
        count: todayDebts.length,
        colones: todayColones,
        dollars: todayDollars,
        debts: todayDebts
      },
      previousDays: {
        count: previousDaysDebts.length,
        colones: previousColones,
        dollars: previousDollars,
        debts: previousDaysDebts
      }
    };
    
    console.log("=== RESUMEN FINAL (SNAPSHOTS) ===");
    console.log("DÍAS ANTERIORES:", `₡${summary.previousDays.colones.toFixed(2)}`);
    console.log("HOY:", `₡${summary.today.colones.toFixed(2)}`);
    console.log("TOTAL:", `₡${(summary.previousDays.colones + summary.today.colones).toFixed(2)}`);
    
    setDebtsSummary(summary);
    
    // Determinar el alcance inicial del pago
    if (summary.previousDays.colones > 0 && summary.today.colones > 0) {
      console.log("MODO: Múltiples períodos - pagar solo días anteriores por defecto");
      setPaymentScope("previous_days");
      setPaymentData({
        amount_paid_colones: summary.previousDays.colones,
        amount_paid_dollars: summary.previousDays.dollars
      });
    } else {
      console.log("MODO: Un solo período - pagar todo");
      setPaymentScope("all");
      setPaymentData({
        amount_paid_colones: debt.amount_colones,
        amount_paid_dollars: debt.amount_dollars
      });
    }
    
    // Reload vaults when opening pay dialog (in case new ones were added)
    fetchVaults();
    setShowPayDialog(true);
  };

  const openIncreaseDialog = (debt) => {
    setSelectedDebt(debt);
    setIncreaseData({
      amount_colones: 0,
      amount_dollars: 0,
      reason: ""
    });
    setShowIncreaseDialog(true);
  };

  const openAdjustDialog = (debt) => {
    setSelectedDebt(debt);
    setAdjustData({
      amount_colones: debt.amount_colones,
      amount_dollars: debt.amount_dollars,
      reason: "",
      adjustment_type: "total",
      // CRITICAL: Initialize saldo_inicio_dia from debt data
      saldo_inicio_dia_colones: debt.saldo_inicio_dia_colones ?? 0,
      saldo_inicio_dia_dollars: debt.saldo_inicio_dia_dollars ?? 0
    });
    setShowAdjustDialog(true);
  };

  const openHistoryDialog = async (debt) => {
    setSelectedDebt(debt);
    setShowHistoryDialog(true);
  };
  
  const downloadCustomerReport = async (format = 'pdf') => {
    if (!selectedDebt || !selectedBox) {
      toast.error("Selecciona una caja y cliente primero");
      return;
    }
    
    // Use opening date of cash register for report
    const reportDate = getWorkingDate();
    
    try {
      const url = `/debts/customer-report-${format}/${encodeURIComponent(selectedDebt.customer_name)}?date=${reportDate}&box_id=${selectedBox.id}`;
      
      const response = await axiosInstance.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `movimientos_${selectedDebt.customer_name}_${reportDate}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al descargar reporte"));
    }
  };
  
  const loadFullMovements = async (debtId, date = null) => {
    try {
      // Obtener la deuda
      const debtResponse = await axiosInstance.get(`/debts/${debtId}`);
      const debt = debtResponse.data;
      const customerName = debt.customer_name;
      
      // Obtener el registro de caja actual
      const cashRegResponse = await axiosInstance.get(`/cash-register?box_id=${selectedBox.id}`);
      const cashReg = cashRegResponse.data;
      
      if (!cashReg || !cashReg.transactions) {
        setFullMovements([]);
        return;
      }
      
      // Filtrar transacciones del cliente
      let clientTransactions = cashReg.transactions.filter(t => {
        const isDebtTransaction = t.type === "cuenta_por_cobrar" || t.type === "aumento_cuenta_por_cobrar";
        const mentionsClient = t.description && t.description.toLowerCase().includes(customerName.toLowerCase());
        return isDebtTransaction && mentionsClient;
      });
      
      // Filtrar por fecha si se especifica
      if (date) {
        clientTransactions = clientTransactions.filter(t => {
          const txDate = t.created_at.split('T')[0];
          return txDate === date;
        });
      }
      
      // Obtener historial de pagos
      const historyResponse = await axiosInstance.get(`/debts/${debtId}/payment-history`);
      const payments = historyResponse.data.payment_history || [];
      
      // Combinar movimientos
      const movements = [];
      
      // Agregar aumentos
      clientTransactions.forEach(t => {
        movements.push({
          id: t.id,
          type: "aumento",
          transaction_type: t.type,
          amount_colones: t.amount_colones,
          amount_dollars: t.amount_dollars,
          description: t.description,
          created_by: t.created_by,
          date: t.created_at
        });
      });
      
      // Agregar pagos
      payments.forEach(p => {
        if (!date || p.paid_at.split('T')[0] === date) {
          movements.push({
            id: p.id,
            type: "pago",
            transaction_type: p.transaction_type,
            amount_colones: p.amount_paid_colones,
            amount_dollars: p.amount_paid_dollars,
            payment_period: p.payment_period || "",
            remaining_colones: p.remaining_colones || 0,
            remaining_dollars: p.remaining_dollars || 0,
            paid_by: p.paid_by,
            date: p.paid_at
          });
        }
      });
      
      // Ordenar por fecha
      movements.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setFullMovements(movements);
    } catch (error) {
      console.error("Error al cargar movimientos:", error);
      toast.error(getErrorMessage(error, "Error al cargar movimientos"));
    }
  };
  
  const openMovementsDialog = async (debt) => {
    setSelectedDebt(debt);
    setShowMovementsDialog(true);
    
    try {
      // Obtener historial de pagos
      const historyResponse = await axiosInstance.get(`/debts/${debt.id}/payment-history`);
      // Backend returns { payment_history: [...], debt: {...}, total_payments: n }
      const movements = historyResponse.data.payment_history || [];
      setCustomerMovements(Array.isArray(movements) ? movements : []);
    } catch (error) {
      setCustomerMovements([]);
      toast.error(getErrorMessage(error, "Error al cargar movimientos"));
    }
  };

  const handlePayDebt = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    if (paymentData.amount_paid_colones <= 0 && paymentData.amount_paid_dollars <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    
    // Validación para SINPE: debe seleccionar banco/bóveda + número de comprobante
    if (paymentType === "sinpe" && !selectedVault) {
      toast.error("Selecciona un banco/bóveda de destino para el SINPE");
      return;
    }
    if ((paymentType === "sinpe" || paymentType === "pago_banco" || paymentType === "transferencia") && !paymentReference.trim()) {
      toast.error("Ingresa el número de comprobante (SINPE/Banco/Transferencia)");
      return;
    }
    if (paymentType === "tarjeta_datafono" && !selectedDebtDatafonoId) {
      toast.error("Selecciona un datáfono");
      return;
    }
    
    try {
      // Para Cuentas por Pagar, SIEMPRE es SALIDA de caja
      let finalTransactionType = paymentType;
      
      if (selectedDebt.type === "por_pagar") {
        // Cuenta por Pagar: SIEMPRE crea SALIDA de dinero
        // (La cuenta por pagar al crearse sumó a venta/caja, ahora al pagar SALE el dinero)
        finalTransactionType = "salida";
        console.log(`Cuenta por Pagar - Siempre es SALIDA de caja`);
      }
      
      console.log(`Pagando deuda ${selectedDebt.id} con payment_period: ${paymentScope}, transaction_type: ${finalTransactionType}`);
      
      const response = await axiosInstance.patch(`/debts/${selectedDebt.id}/pay`, {
        transaction_type: finalTransactionType,
        box_id: selectedBox.id,
        amount_paid_colones: paymentData.amount_paid_colones,
        amount_paid_dollars: paymentData.amount_paid_dollars,
        payment_period: paymentScope,
        payment_note: paymentData.payment_note,
        payment_reference: paymentReference.trim() || null,
        vault_name: paymentType === "sinpe" ? selectedVault : null,
        // Datáfono fields (only when tarjeta_datafono)
        datafono_id: paymentType === "tarjeta_datafono" ? (selectedDebtDatafonoId || null) : null,
        datafono_reference: paymentType === "tarjeta_datafono" ? (debtDatafonoReference || null) : null,
        card_type: paymentType === "tarjeta_datafono" ? debtCardType : null,
      });
      
      if (response.data.is_partial) {
        toast.success(`Pago parcial registrado. Saldo restante: ₡${response.data.remaining_colones.toFixed(2)} / $${response.data.remaining_dollars.toFixed(2)}`);
      } else {
        toast.success("Deuda pagada completamente");
      }

      // Show printable receipt for the customer
      if (response.data.receipt) {
        setPaymentReceipt(response.data.receipt);
      }

      setShowPayDialog(false);
      setSelectedDebt(null);
      setPaymentData({ amount_paid_colones: 0, amount_paid_dollars: 0, payment_note: "" });
      setSelectedVault("");  // NEW: Reset vault selection
      setPaymentReference("");
      setPaymentScope("all");
      fetchDebts();
    } catch (error) {
      console.error("Error general al pagar deuda:", error);
      toast.error(getErrorMessage(error, "Error al pagar deuda"));
    }
  };

  const handleIncreaseDebt = async () => {
    if (increaseData.amount_colones <= 0 && increaseData.amount_dollars <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    
    if (!increaseData.reason.trim()) {
      toast.error("Ingresa la razón del aumento");
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/debts/${selectedDebt.id}/increase`, increaseData);
      toast.success(`Deuda aumentada. Nuevo total: ₡${response.data.new_amount_colones.toFixed(2)} / $${response.data.new_amount_dollars.toFixed(2)}`);
      setShowIncreaseDialog(false);
      setSelectedDebt(null);
      setIncreaseData({ amount_colones: 0, amount_dollars: 0, reason: "" });
      fetchDebts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al aumentar deuda"));
    }
  };
  
  const handleAdjustDebt = async () => {
    if (adjustData.amount_colones < 0 || adjustData.amount_dollars < 0) {
      toast.error("Los montos no pueden ser negativos");
      return;
    }
    
    if (!adjustData.reason.trim()) {
      toast.error("Ingresa la razón del ajuste");
      return;
    }
    
    try {
      const payload = {
        amount_colones: adjustData.amount_colones,
        amount_dollars: adjustData.amount_dollars,
        reason: adjustData.reason
      };
      
      // If redistribution was specified, include it
      if (adjustData.saldo_inicio_dia_colones !== undefined) {
        payload.saldo_inicio_dia_colones = adjustData.saldo_inicio_dia_colones;
        payload.saldo_inicio_dia_dollars = adjustData.saldo_inicio_dia_dollars || 0;
      }
      
      const response = await axiosInstance.patch(`/debts/${selectedDebt.id}/adjust-amount`, payload);
      
      // Mostrar mensaje con información de consolidación
      const consolidated = response.data.debts_consolidated || 1;
      let message = `✅ Deuda ajustada a: ₡${response.data.new_amount_colones.toLocaleString()} / $${response.data.new_amount_dollars.toFixed(2)}`;
      if (consolidated > 1) {
        message += ` (${consolidated} cuentas unificadas)`;
      }
      
      toast.success(message);
      setShowAdjustDialog(false);
      setSelectedDebt(null);
      setAdjustData({ amount_colones: 0, amount_dollars: 0, reason: "", saldo_inicio_dia_colones: 0, saldo_inicio_dia_dollars: 0 });
      fetchDebts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al ajustar monto"));
    }
  };

  const handleDeleteDebt = async () => {
    try {
      await axiosInstance.delete(`/debts/${selectedDebt.id}?password=${deletePassword}`);
      toast.success("Deuda eliminada correctamente");
      setShowDeleteDialog(false);
      setDeletePassword("");
      
      // Force refresh by clearing and reloading
      setDebts([]);
      await fetchDebts();
      
      // Clear selected debt after refresh
      setSelectedDebt(null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar deuda"));
    }
  };

  const openDeleteDialog = (debt) => {
    setSelectedDebt(debt);
    setShowDeleteDialog(true);
  };

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toFixed(2)}`;
  };
  
  const getTodayDate = () => {
    return todayCR();
  };

  // El código de cliente debe ser IDÉNTICO en todos lados. Lo resolvemos SIEMPRE desde el
  // registro del cliente (misma fuente que la página Clientes), priorizando UUID > id/cédula > nombre.
  // Solo usamos el valor enriquecido por el backend como último recurso.
  const custCodeById = {};
  const custCodeByCedula = {};
  const custCodeByName = {};
  customers.forEach(c => {
    if (!c) return;
    const code = c.customer_code || "";
    if (c.id) custCodeById[c.id] = code;
    if (c.cedula) custCodeByCedula[c.cedula] = code;
    if (c.name) custCodeByName[c.name.toLowerCase()] = code;
  });
  const getDebtCode = (d) => (
    custCodeById[d.customer_uuid] ||
    custCodeById[d.customer_id] ||
    custCodeByName[(d.customer_name || "").toLowerCase()] ||
    custCodeByCedula[d.customer_id] ||
    d.customer_code || ""
  );
  // Compara por valor numérico para tolerar diferencias de relleno de ceros (7 == 0007)
  const codeMatchesNumber = (code, term) => {
    const n = parseInt(String(code || "").replace(/\D/g, ""), 10);
    const t = parseInt(term, 10);
    return !isNaN(n) && !isNaN(t) && n === t;
  };

  const filteredDebts = debts
    .filter(d => d.status === activeTab)
    .filter(d => {
      // Filtro por cliente (número de cliente o nombre)
      const term = searchFilter.trim();
      if (term) {
        const isNumeric = /^\d+$/.test(term);
        if (isNumeric) {
          // Búsqueda por número de cliente (1 -> 0001), tolerante al relleno de ceros
          if (!codeMatchesNumber(getDebtCode(d), term)) {
            return false;
          }
        } else if (!d.customer_name.toLowerCase().includes(term.toLowerCase())) {
          return false;
        }
      }
      
      // Filtro por fecha
      if (selectedDate && showDateFilter) {
        const debtDate = new Date(d.created_at).toISOString().split('T')[0];
        if (debtDate !== selectedDate) {
          return false;
        }
      }
      
      return true;
    });
    
  const debtsPorCobrar = filteredDebts.filter(d => d.type === "por_cobrar");
  const debtsPorPagar = filteredDebts.filter(d => d.type === "por_pagar");
  
  // Agrupar deudas por cliente para mostrar el total correcto
  const groupDebtsByCustomer = (debts) => {
    const grouped = {};
    
    debts.forEach(debt => {
      const customerName = debt.customer_name;
      if (!grouped[customerName]) {
        grouped[customerName] = {
          customer_name: customerName,
          customer_code: getDebtCode(debt),
          debts: [],
          total_balance_colones: 0,
          total_balance_dollars: 0,
          latest_date: debt.created_at
        };
      }
      
      grouped[customerName].debts.push(debt);
      grouped[customerName].total_balance_colones += (debt.balance_colones || debt.amount_colones || 0);
      grouped[customerName].total_balance_dollars += (debt.balance_dollars || debt.amount_dollars || 0);
      
      // Actualizar última fecha
      if (new Date(debt.created_at) > new Date(grouped[customerName].latest_date)) {
        grouped[customerName].latest_date = debt.created_at;
      }
    });
    
    // Convertir a array y ordenar por nombre
    return Object.values(grouped).sort((a, b) => a.customer_name.localeCompare(b.customer_name));
  };
  
  const groupedPorCobrar = groupDebtsByCustomer(debtsPorCobrar);
  const groupedPorPagar = groupDebtsByCustomer(debtsPorPagar);

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Cuentas por Cobrar y Pagar
          </h1>
          <p className="text-slate-600">Gestiona las deudas de clientes y proveedores</p>
        </div>
        
        <Button
          data-testid="add-debt-button"
          onClick={() => {
            setCustomerSearch("");
            setFormData({
              customer_id: "",
              customer_name: "",
              type: "por_cobrar",
              amount_colones: 0,
              amount_dollars: 0,
              description: ""
            });
            setShowDialog(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 gap-2"
        >
          <Plus className="w-4 h-4" />
          Nueva Cuenta
        </Button>
      </div>

      {/* Search and Date Filter */}
      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-md">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Búsqueda por cliente */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por nombre o número de cliente..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full"
                  data-testid="debts-customer-search-input"
                />
            </div>
            {searchFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchFilter("")}
                className="text-slate-600"
              >
                Limpiar
              </Button>
            )}
            <div className="text-sm text-slate-600">
              {filteredDebts.length} {filteredDebts.length === 1 ? 'cuenta' : 'cuentas'}
            </div>
          </div>
          
          {/* Filtro por fecha */}
          <div className="flex items-center gap-3 pt-3 border-t">
            <Button
              onClick={() => {
                setShowDateFilter(!showDateFilter);
                if (!showDateFilter) {
                  setSelectedDate(getTodayDate());
                } else {
                  setSelectedDate("");
                }
              }}
              variant={showDateFilter ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              📅 {showDateFilter ? "Ver Todas" : "Filtrar por Fecha"}
            </Button>
            
            <Button
              onClick={() => {
                setSelectedDate(getTodayDate());
                setShowDateFilter(true);
              }}
              variant="outline"
              size="sm"
              className="gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700"
            >
              🕐 Ver Hoy
            </Button>
            
            {showDateFilter && (
              <>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="max-w-xs"
                />
                <span className="text-sm text-blue-700 font-medium">
                  {selectedDate && `Mostrando cuentas del ${selectedDate}`}
                </span>
              </>
            )}
          </div>
        </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger data-testid="pending-debts-tab" value="pendiente">Pendientes</TabsTrigger>
          <TabsTrigger data-testid="paid-debts-tab" value="pagada">Pagadas</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Por Cobrar */}
            <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-green-700">Cuentas por Cobrar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {groupedPorCobrar.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay cuentas por cobrar</p>
                ) : (
                  groupedPorCobrar.map((group) => {
                    // Usar la primera deuda del grupo para los botones de acción
                    const firstDebt = group.debts[0];
                    
                    return (
                      <div
                        key={group.customer_name}
                        data-testid={`debt-group-${group.customer_name}`}
                        className="p-4 bg-green-50 rounded-lg border border-green-200"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-800 flex items-center gap-2">
                              {group.customer_code && (
                                <span data-testid={`debt-code-${group.customer_name}`} className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">#{group.customer_code}</span>
                              )}
                              {group.customer_name}
                            </p>
                            {group.debts.length > 1 && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {group.debts.length} deudas pendientes
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {firstDebt.status === "pendiente" && (
                              <>
                                <Button
                                  data-testid={`pay-debt-${firstDebt.id}`}
                                  size="sm"
                                  onClick={() => openPayDialog(firstDebt)}
                                  className="bg-green-600 hover:bg-green-700"
                                  title="Pagar Deuda"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`increase-debt-${firstDebt.id}`}
                                  size="sm"
                                  onClick={() => openIncreaseDialog(firstDebt)}
                                  className="bg-orange-500 hover:bg-orange-600"
                                  title="Aumentar Deuda"
                                >
                                  <TrendingUp className="w-4 h-4" />
                                </Button>
                                {hasMasterRights && (
                                  <Button
                                    data-testid={`adjust-debt-${firstDebt.id}`}
                                    size="sm"
                                    onClick={() => openAdjustDialog(firstDebt)}
                                    className="bg-purple-500 hover:bg-purple-600"
                                    title="Ajustar Monto (No afecta caja)"
                                  >
                                    ✏️
                                  </Button>
                                )}
                                {hasMasterRights && firstDebt.type === "por_cobrar" && (
                                  <>
                                    <Button
                                      data-testid={`discount-debt-${firstDebt.id}`}
                                      size="sm"
                                      onClick={() => { setSelectedDebt(firstDebt); setShowDiscountDialog(true); }}
                                      className="bg-purple-700 hover:bg-purple-800"
                                      title="Aplicar Descuento"
                                    >
                                      %
                                    </Button>
                                    <Button
                                      data-testid={`return-debt-${firstDebt.id}`}
                                      size="sm"
                                      onClick={() => { setSelectedDebt(firstDebt); setShowReturnDialog(true); }}
                                      className="bg-amber-600 hover:bg-amber-700"
                                      title="Devolver Artículos al Inventario"
                                    >
                                      ↩
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                            {firstDebt.status === "pagada" && (
                              <Button
                                data-testid={`history-debt-${firstDebt.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openHistoryDialog(firstDebt)}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                title="Ver Historial"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              data-testid={`movements-debt-${firstDebt.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => openMovementsDialog(firstDebt)}
                              className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                              title="Ver Movimientos Detallados"
                            >
                              📋
                            </Button>
                            <Button
                              data-testid={`full-history-${firstDebt.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const cid = firstDebt.customer_uuid || `name:${firstDebt.customer_name}`;
                                setFullHistoryTarget({ id: cid, name: firstDebt.customer_name });
                                setShowFullHistoryDialog(true);
                              }}
                              className="border-slate-300 text-slate-600 hover:bg-slate-50"
                              title="Historial completo del cliente"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            {hasMasterRights && (
                              <Button
                                data-testid={`delete-debt-${firstDebt.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => openDeleteDialog(firstDebt)}
                                className="hover:bg-red-50 hover:text-red-600"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-semibold text-green-700">
                              {formatCurrency(group.total_balance_colones)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatCurrency(group.total_balance_dollars, "dollars")}
                            </p>
                          </div>
                          <p className="text-xs text-slate-400">
                            {new Date(group.latest_date).toLocaleDateString('es-CR')}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Por Pagar */}
            <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-red-700">Cuentas por Pagar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {groupedPorPagar.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay cuentas por pagar</p>
                ) : (
                  groupedPorPagar.map((group) => {
                    // Usar la primera deuda del grupo para los botones de acción
                    const firstDebt = group.debts[0];
                    
                    return (
                      <div
                        key={group.customer_name}
                        data-testid={`debt-group-${group.customer_name}`}
                        className="p-4 bg-red-50 rounded-lg border border-red-200"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-800 flex items-center gap-2">
                              {group.customer_code && (
                                <span data-testid={`debt-code-${group.customer_name}`} className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">#{group.customer_code}</span>
                              )}
                              {group.customer_name}
                            </p>
                            {group.debts.length > 1 && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {group.debts.length} deudas pendientes
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {firstDebt.status === "pendiente" && (
                              <>
                                <Button
                                  data-testid={`pay-debt-${firstDebt.id}`}
                                  size="sm"
                                  onClick={() => openPayDialog(firstDebt)}
                                  className="bg-red-600 hover:bg-red-700"
                                  title="Pagar Deuda"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  data-testid={`increase-debt-${firstDebt.id}`}
                                  size="sm"
                                  onClick={() => openIncreaseDialog(firstDebt)}
                                  className="bg-orange-500 hover:bg-orange-600"
                                  title="Aumentar Deuda"
                                >
                                  <TrendingUp className="w-4 h-4" />
                                </Button>
                                {hasMasterRights && (
                                  <Button
                                    data-testid={`adjust-debt-${firstDebt.id}`}
                                    size="sm"
                                    onClick={() => openAdjustDialog(firstDebt)}
                                    className="bg-purple-500 hover:bg-purple-600"
                                    title="Ajustar Monto (No afecta caja)"
                                  >
                                    ✏️
                                  </Button>
                                )}
                              </>
                            )}
                            {firstDebt.status === "pagada" && (
                              <Button
                                data-testid={`history-debt-${firstDebt.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openHistoryDialog(firstDebt)}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                title="Ver Historial"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              data-testid={`movements-debt-${firstDebt.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => openMovementsDialog(firstDebt)}
                              className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                              title="Ver Movimientos Detallados"
                            >
                              📋
                            </Button>
                            <Button
                              data-testid={`full-history-${firstDebt.id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const cid = firstDebt.customer_uuid || `name:${firstDebt.customer_name}`;
                                setFullHistoryTarget({ id: cid, name: firstDebt.customer_name });
                                setShowFullHistoryDialog(true);
                              }}
                              className="border-slate-300 text-slate-600 hover:bg-slate-50"
                              title="Historial completo del cliente"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            {hasMasterRights && (
                              <Button
                                data-testid={`delete-debt-${firstDebt.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => openDeleteDialog(firstDebt)}
                                className="hover:bg-red-50 hover:text-red-600"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-semibold text-red-700">
                              {formatCurrency(group.total_balance_colones)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatCurrency(group.total_balance_dollars, "dollars")}
                            </p>
                          </div>
                          <p className="text-xs text-slate-400">
                            {new Date(group.latest_date).toLocaleDateString('es-CR')}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="debt-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestión de Cuentas</DialogTitle>
            <DialogDescription>Selecciona un cliente para gestionar sus cuentas o crea uno nuevo</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="type">Tipo de Cuenta</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger data-testid="debt-type-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="por_cobrar">Por Cobrar</SelectItem>
                  <SelectItem value="por_pagar">Por Pagar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="customer_select">Seleccionar Cliente</Label>
              <Select
                value={formData.customer_id || "new"}
                onValueChange={(value) => {
                  if (value === "new") {
                    setFormData({
                      ...formData,
                      customer_id: "",
                      customer_name: "",
                      amount_colones: 0,
                      amount_dollars: 0,
                      description: ""
                    });
                    setCustomerSearch("");
                  } else {
                    const selectedCustomer = customers.find(c => c.id === value);
                    if (selectedCustomer) {
                      setFormData({
                        ...formData,
                        customer_id: selectedCustomer.id,
                        customer_name: selectedCustomer.name
                      });
                      setCustomerSearch(selectedCustomer.name);
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="debt-customer-select" className="mt-1.5">
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ Nuevo Cliente</SelectItem>
                  {customers.map(customer => {
                    // Encontrar deudas del cliente del tipo seleccionado
                    const customerDebts = debts.filter(d => 
                      d.customer_name === customer.name && 
                      d.type === formData.type &&
                      d.status === "pendiente"
                    );
                    const totalColones = customerDebts.reduce((sum, d) => sum + (d.balance_colones || d.amount_colones), 0);
                    const totalDollars = customerDebts.reduce((sum, d) => sum + d.amount_dollars, 0);
                    const debtLabel = totalColones > 0 || totalDollars > 0 
                      ? ` - Debe: ₡${totalColones.toFixed(0)} / $${totalDollars.toFixed(0)}`
                      : " - Sin deudas";
                    
                    return (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}{debtLabel}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Si es nuevo cliente, mostrar campos para crearlo */}
            {(!formData.customer_id || formData.customer_id === "") && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <h4 className="font-semibold text-blue-800">Datos del Nuevo Cliente</h4>
                <div>
                  <Label htmlFor="new_customer_name">Nombre del Cliente</Label>
                  <Input
                    id="new_customer_name"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setFormData({...formData, customer_name: e.target.value});
                    }}
                    className="mt-1.5"
                    placeholder="Nombre completo"
                  />
                </div>
              </div>
            )}

            {/* Si el cliente seleccionado tiene deudas, mostrar opciones */}
            {formData.customer_id && (() => {
              const customerDebts = debts.filter(d => 
                d.customer_name === formData.customer_name && 
                d.type === formData.type &&
                d.status === "pendiente"
              );
              const hasDebts = customerDebts.length > 0;
              
              if (hasDebts) {
                const totalColones = customerDebts.reduce((sum, d) => sum + (d.balance_colones || d.amount_colones), 0);
                const totalDollars = customerDebts.reduce((sum, d) => sum + (d.balance_dollars || d.amount_dollars), 0);
                
                return (
                  <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg space-y-3">
                    <h4 className="font-bold text-yellow-800">Cliente con deuda existente</h4>
                    <p className="text-sm text-yellow-700">
                      {formData.customer_name} tiene {customerDebts.length} cuenta(s) pendiente(s):
                    </p>
                    <div className="bg-white p-3 rounded border border-yellow-300">
                      <p className="text-lg font-bold text-yellow-800">
                        Total: ₡{totalColones.toFixed(2)} / ${totalDollars.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedDebt(customerDebts[0]);
                          setIncreaseData({ amount_colones: 0, amount_dollars: 0, reason: "" });
                          setShowIncreaseDialog(true);
                          setShowDialog(false);
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Aumentar Deuda
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedDebt(customerDebts[0]);
                          setPaymentData({ amount_paid_colones: totalColones, amount_paid_dollars: totalDollars });
                          // Para deudas "por_pagar", el tipo por defecto es "salida" (dinero sale de caja)
                          setPaymentType(customerDebts[0].type === "por_pagar" ? "salida" : "venta");
                          setShowPayDialog(true);
                          setShowDialog(false);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Pagar Deuda
                      </Button>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Campos para monto y descripción (solo si es nuevo cliente o cliente sin deudas) */}
            {(!formData.customer_id || (() => {
              const customerDebts = debts.filter(d => 
                d.customer_name === formData.customer_name && 
                d.type === formData.type &&
                d.status === "pendiente"
              );
              return customerDebts.length === 0;
            })()) && (
              <>
                <div>
                  <Label htmlFor="amount_colones">Monto en Colones</Label>
                  <Input
                    id="amount_colones"
                    data-testid="debt-colones-input"
                    type="number"
                    step="0.01"
                    value={formData.amount_colones}
                    onChange={(e) => setFormData({...formData, amount_colones: parseFloat(e.target.value) || 0})}
                    className="mt-1.5"
                  />
                </div>
                
                {selectedBox?.uses_dollars !== false && (
                  <div>
                    <Label htmlFor="amount_dollars">Monto en Dólares</Label>
                    <Input
                      id="amount_dollars"
                      data-testid="debt-dollars-input"
                      type="number"
                      step="0.01"
                      value={formData.amount_dollars}
                      onChange={(e) => setFormData({...formData, amount_dollars: parseFloat(e.target.value) || 0})}
                      className="mt-1.5"
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Input
                    id="description"
                    data-testid="debt-description-input"
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="mt-1.5"
                    placeholder="Detalles de la cuenta"
                  />
                </div>
                
                {/* Solo MASTER puede ajustar deudas de días anteriores */}
                {hasMasterRights && (
                  <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_previous_days"
                        checked={formData.is_previous_days}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData({
                            ...formData, 
                            is_previous_days: checked,
                            previous_days_colones: checked ? formData.amount_colones : 0,
                            previous_days_dollars: checked ? formData.amount_dollars : 0
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="is_previous_days" className="font-semibold text-orange-800 cursor-pointer">
                        📅 Esta deuda es de días anteriores (no registrada previamente)
                      </Label>
                    </div>
                    
                    {formData.is_previous_days && (
                      <div className="space-y-3 pl-6">
                        <p className="text-sm text-orange-700">
                          ⚠️ Esta opción es para registrar deudas que existían antes de hoy pero no fueron registradas.
                          El monto aparecerá en &quot;Días Anteriores&quot; en el reporte.
                        </p>
                        
                        <div>
                          <Label htmlFor="previous_days_colones">Monto Días Anteriores (Colones)</Label>
                          <Input
                            id="previous_days_colones"
                            type="number"
                            step="0.01"
                            value={formData.previous_days_colones}
                            onChange={(e) => setFormData({
                              ...formData, 
                              previous_days_colones: parseFloat(e.target.value) || 0
                            })}
                            className="mt-1.5"
                            placeholder="Monto de días anteriores"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="previous_days_dollars">Monto Días Anteriores (Dólares)</Label>
                          <Input
                            id="previous_days_dollars"
                            type="number"
                            step="0.01"
                            value={formData.previous_days_dollars}
                            onChange={(e) => setFormData({
                              ...formData, 
                              previous_days_dollars: parseFloat(e.target.value) || 0
                            })}
                            className="mt-1.5"
                            placeholder="Monto de días anteriores en dólares"
                          />
                        </div>
                        
                        <div className="bg-white p-3 rounded border border-orange-300">
                          <p className="text-sm font-medium text-gray-700">
                            Total deuda: ₡{formData.amount_colones.toFixed(2)} / ${formData.amount_dollars.toFixed(2)}
                          </p>
                          <p className="text-sm font-medium text-orange-700 mt-1">
                            → Días anteriores: ₡{formData.previous_days_colones.toFixed(2)} / ${formData.previous_days_dollars.toFixed(2)}
                          </p>
                          <p className="text-sm font-medium text-blue-700">
                            → Deuda HOY: ₡{(formData.amount_colones - formData.previous_days_colones).toFixed(2)} / ${(formData.amount_dollars - formData.previous_days_dollars).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            {/* Solo mostrar botón Registrar si no hay deudas existentes o es nuevo cliente */}
            {(!formData.customer_id || (() => {
              const customerDebts = debts.filter(d => 
                d.customer_name === formData.customer_name && 
                d.type === formData.type &&
                d.status === "pendiente"
              );
              return customerDebts.length === 0;
            })()) && (
              <Button
                data-testid="confirm-debt-button"
                onClick={handleCreateDebt}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!formData.customer_name || (formData.amount_colones === 0 && formData.amount_dollars === 0)}
              >
                Registrar Nueva Cuenta
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Debt Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent data-testid="pay-debt-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pagar Deuda</DialogTitle>
            <DialogDescription>
              Pago para {selectedDebt?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* DEBUG INFO - ELIMINAR DESPUÉS */}
            <div className="p-2 bg-yellow-100 text-xs">
              <p><b>DEBUG:</b></p>
              <p>previousDays.count: {debtsSummary.previousDays.count}</p>
              <p>today.count: {debtsSummary.today.count}</p>
              <p>previousDays.colones: ₡{debtsSummary.previousDays.colones}</p>
              <p>today.colones: ₡{debtsSummary.today.colones}</p>
            </div>
            
            {/* Mostrar resumen de deudas si hay de múltiples días */}
            {(debtsSummary.previousDays.colones > 0 || debtsSummary.previousDays.dollars > 0) && 
             (debtsSummary.today.colones > 0 || debtsSummary.today.dollars > 0) && (
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg space-y-3">
                <h4 className="font-bold text-blue-800">Este cliente tiene deudas de múltiples días</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded border border-blue-200">
                    <p className="text-xs text-blue-600 font-semibold">Deudas de Días Anteriores</p>
                    <p className="text-sm font-bold text-blue-800">
                      ₡{debtsSummary.previousDays.colones.toFixed(2)}
                    </p>
                    <p className="text-xs text-blue-600">
                      ${debtsSummary.previousDays.dollars.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {debtsSummary.previousDays.count} cuenta(s)
                    </p>
                  </div>
                  
                  <div className="p-3 bg-white rounded border border-orange-200">
                    <p className="text-xs text-orange-600 font-semibold">Deuda de Hoy</p>
                    <p className="text-sm font-bold text-orange-800">
                      ₡{debtsSummary.today.colones.toFixed(2)}
                    </p>
                    <p className="text-xs text-orange-600">
                      ${debtsSummary.today.dollars.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {debtsSummary.today.count} cuenta(s)
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>¿Qué deseas pagar?</Label>
                  <div className="space-y-2">
                    <div 
                      className={`p-3 border-2 rounded cursor-pointer transition-all ${
                        paymentScope === "previous_days" 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-slate-200 hover:border-blue-300"
                      }`}
                      onClick={() => {
                        setPaymentScope("previous_days");
                        setPaymentData({
                          amount_paid_colones: debtsSummary.previousDays.colones,
                          amount_paid_dollars: debtsSummary.previousDays.dollars
                        });
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <input 
                          type="radio" 
                          checked={paymentScope === "previous_days"}
                          onChange={() => {}}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm">Solo deudas de días anteriores</p>
                          <p className="text-xs text-slate-600">
                            ₡{debtsSummary.previousDays.colones.toFixed(2)} / ${debtsSummary.previousDays.dollars.toFixed(2)}
                          </p>
                          <p className="text-xs text-green-600 mt-1">✓ La deuda de hoy queda pendiente</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`p-3 border-2 rounded cursor-pointer transition-all ${
                        paymentScope === "all" 
                          ? "border-green-500 bg-green-50" 
                          : "border-slate-200 hover:border-green-300"
                      }`}
                      onClick={() => {
                        setPaymentScope("all");
                        setPaymentData({
                          amount_paid_colones: debtsSummary.previousDays.colones + debtsSummary.today.colones,
                          amount_paid_dollars: debtsSummary.previousDays.dollars + debtsSummary.today.dollars
                        });
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <input 
                          type="radio" 
                          checked={paymentScope === "all"}
                          onChange={() => {}}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-sm">Pagar TODO (incluye deuda de hoy)</p>
                          <p className="text-xs text-slate-600">
                            ₡{(debtsSummary.previousDays.colones + debtsSummary.today.colones).toFixed(2)} / ${(debtsSummary.previousDays.dollars + debtsSummary.today.dollars).toFixed(2)}
                          </p>
                          <p className="text-xs text-orange-600 mt-1">⚠️ Esto rebajará la cuenta por cobrar de hoy</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Mostrar deuda total si solo hay de un tipo */}
            {!((debtsSummary.previousDays.colones > 0 || debtsSummary.previousDays.dollars > 0) && 
               (debtsSummary.today.colones > 0 || debtsSummary.today.dollars > 0)) && (
              <div>
                <div className="p-4 bg-orange-50 border border-orange-300 rounded-lg mb-3">
                  <p className="text-sm font-semibold text-orange-800">
                    ⚠️ No se detectaron deudas de múltiples días
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Si este cliente tiene deudas de días anteriores y de hoy, por favor reporta este problema.
                  </p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Deuda Total:</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {selectedDebt && formatCurrency(selectedDebt.amount_colones)}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedDebt && formatCurrency(selectedDebt.amount_dollars, "dollars")}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pay_colones">Monto a Pagar (₡)</Label>
                <Input
                  id="pay_colones"
                  type="number"
                  step="0.01"
                  value={paymentData.amount_paid_colones}
                  onChange={(e) => setPaymentData({...paymentData, amount_paid_colones: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="pay_dollars">Monto a Pagar ($)</Label>
                <Input
                  id="pay_dollars"
                  type="number"
                  step="0.01"
                  value={paymentData.amount_paid_dollars}
                  onChange={(e) => setPaymentData({...paymentData, amount_paid_dollars: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Solo mostrar selector de tipo de movimiento para Cuentas por COBRAR */}
            {selectedDebt?.type !== "por_pagar" && (
            <div>
              <Label htmlFor="payment_type">Tipo de Movimiento</Label>
              <Select
                value={paymentType}
                onValueChange={setPaymentType}
              >
                <SelectTrigger data-testid="payment-type-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Opciones para Cuentas por Cobrar (dinero ENTRA a caja) */}
                  <SelectItem value="venta">Venta (Afecta Caja)</SelectItem>
                  <SelectItem value="sinpe">SINPE Móvil (No Afecta Caja)</SelectItem>
                  <SelectItem value="tarjeta_datafono">Tarjeta / Datáfono (No Afecta Caja)</SelectItem>
                  <SelectItem value="banco_tucan">Tucán (Afecta Caja)</SelectItem>
                  <SelectItem value="banco_payser">Payser (Afecta Caja)</SelectItem>
                  <SelectItem value="banco_western">Wester (Afecta Caja)</SelectItem>
                  <SelectItem value="banco_rapibac">Rapibac (Afecta Caja)</SelectItem>
                  <SelectItem value="entrada">Entrada de Dinero</SelectItem>
                  <SelectItem value="pago_con_premio_afecta">Pago con Premio (Afecta Caja)</SelectItem>
                  <SelectItem value="pago_dias_anteriores_afecta">Pago Días Anteriores (Afecta Caja)</SelectItem>
                  <SelectItem value="pago_dias_anteriores_no_afecta">Pago Días Anteriores (NO Afecta Caja)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5">
                Usa &quot;Pago con Premio&quot; para netear o &quot;Pago Días Anteriores&quot; si el cliente pagó antes
              </p>
            </div>
            )}

            {/* Mensaje informativo para Cuentas por Pagar */}
            {selectedDebt?.type === "por_pagar" && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                <p className="text-sm text-orange-800">
                  <strong>Cuenta por Pagar:</strong> Al pagar se registra automáticamente como SALIDA de dinero.
                </p>
              </div>
            )}

            {/* NEW: Vault selector for SINPE payments - Solo para Cuentas por Cobrar */}
            {paymentType === "tarjeta_datafono" && selectedDebt?.type !== "por_pagar" && (
              <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded" data-testid="debt-datafono-block">
                <Label>Datáfono *</Label>
                {debtDatafonos.length === 0 ? (
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    No hay datáfonos activos. Configúralos en <b>Bancos → Datáfonos</b>.
                  </div>
                ) : (
                  <Select value={selectedDebtDatafonoId} onValueChange={setSelectedDebtDatafonoId}>
                    <SelectTrigger data-testid="debt-datafono-select"><SelectValue placeholder="Selecciona datáfono..." /></SelectTrigger>
                    <SelectContent>
                      {debtDatafonos.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}{d.brand_model ? ` · ${d.brand_model}` : ""}{d.bank_name ? ` → ${d.bank_name}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={debtCardType} onValueChange={setDebtCardType}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credito">Crédito</SelectItem>
                        <SelectItem value="debito">Débito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nº Voucher</Label>
                    <Input value={debtDatafonoReference} onChange={(e) => setDebtDatafonoReference(e.target.value)} placeholder="Referencia" className="h-8" data-testid="debt-datafono-reference" />
                  </div>
                </div>
                <p className="text-[11px] text-emerald-800">✓ El monto se acreditará al banco vinculado al datáfono.</p>
              </div>
            )}

            {/* NEW: Vault selector for SINPE payments - Solo para Cuentas por Cobrar */}
            {paymentType === "sinpe" && selectedDebt?.type !== "por_pagar" && (
              <div>
                <Label>Banco (Bóveda) de Destino *</Label>
                {vaults.length === 0 ? (
                  <div className="mt-1.5 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      No hay bancos creados. Ve a <strong>Bancos (Bóvedas)</strong> para crear uno.
                    </p>
                  </div>
                ) : (
                  <Select value={selectedVault} onValueChange={setSelectedVault}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Seleccionar banco..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vaults.map((vault) => (
                        <SelectItem key={vault.id} value={vault.name}>
                          {vault.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {vaults.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {vaults.length} banco(s) disponible(s). Selecciona dónde entra el SINPE.
                  </p>
                )}
              </div>
            )}

            {(paymentType === "sinpe" || paymentType === "pago_banco" || paymentType === "transferencia") && (
              <div>
                <Label htmlFor="payment_ref">Nº Comprobante ({paymentType.toUpperCase()})</Label>
                <Input
                  id="payment_ref"
                  data-testid="debt-payment-reference"
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Ej: 123456789 o últimos 6 dígitos"
                  className="mt-1.5"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Referencia bancaria obligatoria para auditoría del pago.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="payment_note">Detalle del Pago (Opcional)</Label>
              <Input
                id="payment_note"
                type="text"
                value={paymentData.payment_note}
                onChange={(e) => setPaymentData({...paymentData, payment_note: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: Pago quincenal, abono, etc."
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Este detalle aparecerá en los reportes PDF, Excel y al ver movimientos
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPayDialog(false);
              setSelectedDebt(null);
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-pay-debt-button"
              onClick={handlePayDebt}
              className="bg-green-600 hover:bg-green-700"
            >
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Increase Debt Dialog */}
      <Dialog open={showIncreaseDialog} onOpenChange={setShowIncreaseDialog}>
        <DialogContent data-testid="increase-debt-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aumentar Deuda</DialogTitle>
            <DialogDescription>
              Aumentar el monto de la deuda de {selectedDebt?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Deuda Actual:</p>
              <p className="text-lg font-semibold text-slate-800">
                {selectedDebt && formatCurrency(selectedDebt.amount_colones)}
              </p>
              <p className="text-sm text-slate-600">
                {selectedDebt && formatCurrency(selectedDebt.amount_dollars, "dollars")}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="increase_colones">Aumentar (₡)</Label>
                <Input
                  id="increase_colones"
                  type="number"
                  step="0.01"
                  value={increaseData.amount_colones}
                  onChange={(e) => setIncreaseData({...increaseData, amount_colones: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="increase_dollars">Aumentar ($)</Label>
                <Input
                  id="increase_dollars"
                  type="number"
                  step="0.01"
                  value={increaseData.amount_dollars}
                  onChange={(e) => setIncreaseData({...increaseData, amount_dollars: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="increase_reason">Razón del Aumento</Label>
              <Input
                id="increase_reason"
                type="text"
                value={increaseData.reason}
                onChange={(e) => setIncreaseData({...increaseData, reason: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: Intereses, recargos, etc."
              />
            </div>

            {selectedDebt && (increaseData.amount_colones > 0 || increaseData.amount_dollars > 0) && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-600 mb-1">Nueva Deuda Total:</p>
                <p className="text-lg font-semibold text-blue-800">
                  ₡{(selectedDebt.amount_colones + increaseData.amount_colones).toFixed(2)}
                </p>
                <p className="text-sm text-blue-600">
                  ${(selectedDebt.amount_dollars + increaseData.amount_dollars).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowIncreaseDialog(false);
              setSelectedDebt(null);
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-increase-debt-button"
              onClick={handleIncreaseDebt}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Aumentar Deuda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Adjust Debt Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent data-testid="adjust-debt-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajustar Monto de Deuda (Master)</DialogTitle>
            <DialogDescription>
              Ajustar deuda de {selectedDebt?.customer_name} - NO afecta la caja
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Desglose de deuda */}
            {selectedDebt && selectedDebt.snapshot_date && selectedDebt.saldo_inicio_dia_colones != null && (() => {
              // Use opening date of cash register instead of current date
              const workingDate = getWorkingDate();
              const isToday = selectedDebt.snapshot_date === workingDate;
              
              if (isToday) {
                const anterior = selectedDebt.saldo_inicio_dia_colones;
                const anteriorDollars = selectedDebt.saldo_inicio_dia_dollars || 0;
                const hoy = selectedDebt.amount_colones - anterior;
                const hoyDollars = selectedDebt.amount_dollars - anteriorDollars;
                
                return (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-800 mb-2">Desglose de Deuda Actual:</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-white rounded border border-blue-200">
                        <p className="text-blue-600 font-semibold mb-1">Días Anteriores:</p>
                        <p className="font-bold text-lg">₡{anterior.toFixed(2)}</p>
                        <p className="text-slate-600">${anteriorDollars.toFixed(2)}</p>
                      </div>
                      <div className="p-3 bg-white rounded border border-blue-200">
                        <p className="text-blue-600 font-semibold mb-1">Hoy:</p>
                        <p className="font-bold text-lg">₡{hoy.toFixed(2)}</p>
                        <p className="text-slate-600">${hoyDollars.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-300 text-center">
                      <p className="text-blue-700 text-base">
                        Total: <span className="font-bold text-lg">₡{selectedDebt.amount_colones.toFixed(2)}</span>
                        <span className="text-slate-600 ml-2">${selectedDebt.amount_dollars.toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Deuda Actual Total:</p>
                  <p className="text-lg font-semibold text-slate-800">
                    ₡{selectedDebt.amount_colones.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-600">
                    ${selectedDebt.amount_dollars.toFixed(2)}
                  </p>
                </div>
              );
            })()}
            
            {/* Info about consolidation */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                📊 <b>Ajuste con Consolidación:</b> Si este cliente tiene múltiples cuentas, 
                todas se unificarán en una sola con el nuevo monto que ingreses.
              </p>
            </div>

            {/* Option to move between Hoy and Días Anteriores - ALWAYS show for redistribution */}
            {selectedDebt && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-800 mb-3">🔄 Redistribuir Monto (Días Anteriores vs Hoy):</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="redistribute_anterior" className="text-green-700">
                      Monto a Días Anteriores (₡)
                    </Label>
                    <Input
                      id="redistribute_anterior"
                      type="number"
                      step="0.01"
                      value={adjustData.saldo_inicio_dia_colones ?? selectedDebt.saldo_inicio_dia_colones ?? 0}
                      onChange={(e) => {
                        const anterior = parseFloat(e.target.value) || 0;
                        const total = adjustData.amount_colones || selectedDebt.amount_colones;
                        const hoy = total - anterior;
                        setAdjustData({
                          ...adjustData, 
                          saldo_inicio_dia_colones: anterior,
                          amount_colones: total
                        });
                      }}
                      className="mt-1"
                    />
                    <p className="text-xs text-green-600 mt-1">Deuda de días anteriores</p>
                  </div>
                  <div>
                    <Label htmlFor="redistribute_hoy" className="text-green-700">
                      Monto de Hoy (₡)
                    </Label>
                    <Input
                      id="redistribute_hoy"
                      type="number"
                      step="0.01"
                      value={(() => {
                        const total = adjustData.amount_colones || selectedDebt.amount_colones;
                        const anterior = adjustData.saldo_inicio_dia_colones ?? selectedDebt.saldo_inicio_dia_colones ?? 0;
                        return (total - anterior).toFixed(2);
                      })()}
                      onChange={(e) => {
                        const hoy = parseFloat(e.target.value) || 0;
                        const total = adjustData.amount_colones || selectedDebt.amount_colones;
                        const anterior = total - hoy;
                        setAdjustData({
                          ...adjustData, 
                          saldo_inicio_dia_colones: anterior >= 0 ? anterior : 0,
                          amount_colones: total
                        });
                      }}
                      className="mt-1"
                    />
                    <p className="text-xs text-green-600 mt-1">Deuda generada hoy</p>
                  </div>
                </div>
                <p className="text-xs text-green-700 mt-2 italic">
                  Puedes mover el monto de &quot;Hoy&quot; a &quot;Días Anteriores&quot; o viceversa sin cambiar el total.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="adjust_colones">Nuevo Monto Total (₡)</Label>
                <Input
                  id="adjust_colones"
                  type="number"
                  step="0.01"
                  value={adjustData.amount_colones}
                  onChange={(e) => setAdjustData({...adjustData, amount_colones: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="adjust_dollars">Nuevo Monto Total ($)</Label>
                <Input
                  id="adjust_dollars"
                  type="number"
                  step="0.01"
                  value={adjustData.amount_dollars}
                  onChange={(e) => setAdjustData({...adjustData, amount_dollars: parseFloat(e.target.value) || 0})}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="adjust_reason">Razón del Ajuste (Obligatorio)</Label>
              <Input
                id="adjust_reason"
                type="text"
                value={adjustData.reason}
                onChange={(e) => setAdjustData({...adjustData, reason: e.target.value})}
                className="mt-1.5"
                placeholder="Ej: Corrección de error, descuento aplicado, condonación parcial, etc."
              />
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <p className="text-sm text-yellow-800 font-bold mb-1">⚠️ Funcionalidad Master:</p>
              <p className="text-xs text-yellow-700">
                • Este ajuste modifica el monto total de la deuda<br/>
                • Si hay múltiples cuentas, se unificarán en una sola<br/>
                • NO afecta el efectivo de la caja<br/>
                • NO crea transacciones de entrada/salida
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAdjustDialog(false);
              setSelectedDebt(null);
              setAdjustData({ amount_colones: 0, amount_dollars: 0, reason: "", saldo_inicio_dia_colones: 0, saldo_inicio_dia_dollars: 0 });
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-adjust-debt-button"
              onClick={handleAdjustDebt}
              className="bg-purple-600 hover:bg-purple-700"
            >
              🔒 Ajustar y Unificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Report Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent data-testid="history-debt-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Descargar Reporte de Movimientos</DialogTitle>
            <DialogDescription>
              Cliente: {selectedDebt?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Selecciona el formato para descargar el reporte de movimientos del día de hoy:
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => downloadCustomerReport('pdf')}
                className="h-20 bg-red-600 hover:bg-red-700 flex flex-col gap-2"
              >
                <span className="text-2xl">📄</span>
                <span>Descargar PDF</span>
              </Button>
              
              <Button
                onClick={() => downloadCustomerReport('excel')}
                className="h-20 bg-green-600 hover:bg-green-700 flex flex-col gap-2"
              >
                <span className="text-2xl">📊</span>
                <span>Descargar Excel</span>
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center mt-4">
              El reporte incluirá todos los movimientos (aumentos y pagos) del cliente de hoy
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowHistoryDialog(false);
              setSelectedDebt(null);
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Debt Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-debt-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar Cuenta</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar esta cuenta de {selectedDebt?.customer_name}? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="delete_password">Contraseña Master</Label>
            <Input
              id="delete_password"
              data-testid="delete-debt-password-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="mt-1.5"
              placeholder="Ingresa la contraseña master"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeletePassword("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-debt-button"
              onClick={handleDeleteDebt}
              variant="destructive"
              disabled={!deletePassword}
            >
              Eliminar Cuenta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movements Dialog */}
      <Dialog open={showMovementsDialog} onOpenChange={setShowMovementsDialog}>
        <DialogContent data-testid="movements-debt-dialog" className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Movimientos Detallados</DialogTitle>
            <DialogDescription>
              Historial completo de movimientos para {selectedDebt?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!customerMovements || customerMovements.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay movimientos registrados para este cliente</p>
            ) : (
              <div className="space-y-3">
                {customerMovements.map((movement, index) => (
                  <div key={movement.id || index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {movement.transaction_type || 'Movimiento'}
                        </p>
                        <p className="text-xs text-slate-600">
                          {movement.payment_type === "full" ? "Pago Completo" : 
                           movement.payment_type === "partial" ? "Pago Parcial" : 
                           "Otro"}
                        </p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm font-semibold text-green-700">
                          {formatCurrency(movement.amount_paid_colones || 0)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(movement.amount_paid_dollars || 0, "dollars")}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-xs text-slate-500">
                          {movement.paid_by || 'Sistema'}
                        </p>
                        <p className="text-xs text-slate-400">
                          {movement.paid_at ? new Date(movement.paid_at).toLocaleString('es-CR') : 'Fecha no disponible'}
                        </p>
                      </div>
                    </div>
                    
                    {movement.payment_type === "partial" && (
                      <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <p className="text-xs text-yellow-700">
                          Saldo restante: {formatCurrency(movement.remaining_colones || 0)} / {formatCurrency(movement.remaining_dollars || 0, "dollars")}
                        </p>
                      </div>
                    )}
                    {movement.debt_id && movement.id && (
                      <div className="mt-2 flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`reprint-receipt-${movement.id}`}
                          onClick={async () => {
                            try {
                              const r = await axiosInstance.get(
                                `/debts/${movement.debt_id}/payment-history/${movement.id}/receipt`
                              );
                              if (r.data?.receipt) setPaymentReceipt(r.data.receipt);
                            } catch (e) {
                              toast.error(getErrorMessage(e, "Error al obtener comprobante"));
                            }
                          }}
                          className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          🖨️ Reimprimir comprobante
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              setShowMovementsDialog(false);
              setSelectedDebt(null);
              setCustomerMovements([]);
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment receipt — printable ticket showing balance before/after */}
      <Dialog open={!!paymentReceipt} onOpenChange={(o) => !o && setPaymentReceipt(null)}>
        <DialogContent className="max-w-sm" data-testid="payment-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Comprobante de abono</DialogTitle>
          </DialogHeader>
          {paymentReceipt && (
            <div id="payment-receipt-printable" className="font-mono text-sm bg-white p-4 border rounded">
              <div className="text-center font-bold text-base mb-1">
                {selectedBox?.name || "PaliStore"}
              </div>
              <div className="text-center text-xs text-slate-600 mb-3">
                COMPROBANTE DE ABONO
              </div>
              <div className="border-t border-dashed pt-2 space-y-1">
                <div className="flex justify-between"><span>Fecha:</span><span>{(paymentReceipt.paid_at || "").substring(0, 19).replace("T", " ")}</span></div>
                <div className="flex justify-between"><span>Cajero:</span><span>{paymentReceipt.paid_by}</span></div>
                <div className="flex justify-between"><span>Cliente:</span><span className="font-bold">{paymentReceipt.customer_name}</span></div>
                <div className="flex justify-between"><span>Método:</span><span>{paymentReceipt.transaction_type}</span></div>
                {paymentReceipt.payment_note && <div className="text-xs italic">Nota: {paymentReceipt.payment_note}</div>}
              </div>
              <div className="border-t border-dashed mt-2 pt-2 space-y-1">
                <div className="flex justify-between">
                  <span>Saldo anterior:</span>
                  <span className="font-bold">₡{Number(paymentReceipt.balance_before_colones || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-emerald-700">
                  <span>Abono:</span>
                  <span className="font-bold">- ₡{Number(paymentReceipt.payment_colones || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-base border-t border-slate-400 pt-1 mt-1">
                  <span className="font-bold">Saldo nuevo:</span>
                  <span className="font-bold">₡{Number(paymentReceipt.balance_after_colones || 0).toLocaleString()}</span>
                </div>
                {(paymentReceipt.balance_before_dollars > 0 || paymentReceipt.payment_dollars > 0) && (
                  <>
                    <div className="border-t border-dashed mt-2 pt-1"></div>
                    <div className="flex justify-between"><span>Saldo $ anterior:</span><span>${Number(paymentReceipt.balance_before_dollars || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-emerald-700"><span>Abono $:</span><span>-${Number(paymentReceipt.payment_dollars || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold"><span>Saldo $ nuevo:</span><span>${Number(paymentReceipt.balance_after_dollars || 0).toFixed(2)}</span></div>
                  </>
                )}
              </div>
              {paymentReceipt.is_full_payment && (
                <div className="text-center mt-3 py-1 bg-emerald-100 text-emerald-800 rounded font-bold">
                  ✓ DEUDA CANCELADA
                </div>
              )}
              <div className="text-center text-xs text-slate-500 mt-3 pt-2 border-t border-dashed">
                Gracias por su pago<br/>Conserve este comprobante
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPaymentReceipt(null)}
              data-testid="receipt-close-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => {
                const w = window.open("", "_blank", "width=380,height=600");
                if (!w) { toast.error("No se pudo abrir la ventana de impresión"); return; }
                const node = document.getElementById("payment-receipt-printable");
                w.document.write(`<html><head><title>Comprobante</title>
                  <style>
                    body{font-family:monospace;padding:8px;font-size:12px}
                    .text-center{text-align:center}.font-bold{font-weight:700}
                    .border-t,.border-b{border-top:1px dashed #999;margin:6px 0;padding-top:4px}
                    .flex{display:flex;justify-content:space-between}
                    .text-emerald-700{color:#047857}
                    @media print{@page{margin:0;size:80mm auto}body{margin:8px}}
                  </style></head><body>${node ? node.innerHTML : ""}<script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script></body></html>`);
                w.document.close();
              }}
              data-testid="receipt-print-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial completo del cliente (ventas + notas crédito + pagos) */}
      <CustomerHistoryDialog
        open={showFullHistoryDialog}
        onOpenChange={setShowFullHistoryDialog}
        customerId={fullHistoryTarget?.id}
        customerName={fullHistoryTarget?.name}
        boxId={selectedBox?.id}
      />

      <DebtDiscountDialog
        open={showDiscountDialog}
        onOpenChange={setShowDiscountDialog}
        debt={selectedDebt}
        user={user}
        onSaved={fetchDebts}
      />
      <DebtReturnDialog
        open={showReturnDialog}
        onOpenChange={setShowReturnDialog}
        debt={selectedDebt}
        user={user}
        onSaved={fetchDebts}
      />
    </div>
  );
}